import { useState } from 'react';
import type { JSX } from 'react';
import type { KontoMitarbeiterOption, Principal, TenantRole } from '@asas/domain';
import { mitarbeiterVorschlag } from '@asas/domain/konto-mitarbeiter';

import type { FehlerBefund } from './daten/fehler';

/**
 * Die Meldung zu einer gescheiterten Kontoaktion.
 *
 * Bei `konflikt` NICHT die allgemeine Meldung „Daten geändert, neu laden und
 * erneut versuchen" (Befund aus dem KI-Agent-Review zu PR #51): Die
 * Nutzerverwaltung antwortet mit CONFLICT ausschließlich aus ihren zwei
 * Schutzregeln, und ein erneuter Versuch scheitert dort jedes Mal gleich. Die
 * Meldung nennt deshalb beide Regeln. Kein Orakel: wer sie sieht, hat
 * `principals:manage` und kennt die Regeln ohnehin.
 *
 * Alle übrigen Arten behalten die Meldung aus `klassifiziereFehler` — dort
 * wird jede Fehlerart EINMAL unterschieden (CLAUDE.md §20.2, Punkt 2).
 */
export function meldungFuerKontoaktion(befund: FehlerBefund): string {
  if (befund.art === 'konflikt') {
    return 'Diese Änderung ist nicht möglich: Ein Konto kann sich nicht selbst sperren oder sich die eigene Administrationsrolle entziehen, und die letzte aktive Systemadministration bleibt erhalten.';
  }

  return befund.meldung;
}

interface PrincipalRowProps {
  readonly principal: Principal;
  readonly roles: readonly TenantRole[];
  readonly busy: boolean;
  readonly rowError: string | undefined;
  readonly onLock: (principalId: string) => void;
  readonly onUnlock: (principalId: string) => void;
  readonly onAssignRole: (principalId: string, roleId: string) => void;
  readonly onRevokeRole: (principalId: string, roleId: string) => void;
  /** `null` ohne `principals:manage`: dann nur Anzeige, keine Auswahl (AC-107-1). */
  readonly optionen: readonly KontoMitarbeiterOption[] | null;
  readonly onLinkEmployee: (principalId: string, employeeId: string | null) => void;
}

/**
 * Die Person eines Kontos (AC-107-1, E2): Anzeige, Auswahl, Vorschlag, Lösen.
 * Der Vorschlag (gleicher Name wie in Entra, genau ein freier Treffer) wird
 * nur angeboten — verknüpft wird erst mit „Übernehmen“.
 */
function PersonZelle({
  principal,
  busy,
  optionen,
  onLinkEmployee,
}: {
  readonly principal: Principal;
  readonly busy: boolean;
  readonly optionen: readonly KontoMitarbeiterOption[] | null;
  readonly onLinkEmployee: (principalId: string, employeeId: string | null) => void;
}): JSX.Element {
  const [auswahl, setAuswahl] = useState('');
  const kontoName = principal.displayName ?? principal.subject;

  if (optionen === null) {
    return <td>{principal.mitarbeiter?.name ?? '—'}</td>;
  }

  if (principal.mitarbeiter !== null) {
    return (
      <td>
        {principal.mitarbeiter.name}{' '}
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            onLinkEmployee(principal.id, null);
          }}
        >
          lösen
        </button>
      </td>
    );
  }

  const vorschlag = mitarbeiterVorschlag(principal, optionen);

  return (
    <td>
      {vorschlag === null ? null : (
        <p>
          Vorschlag: {vorschlag.name}{' '}
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              onLinkEmployee(principal.id, vorschlag.id);
            }}
          >
            Übernehmen
          </button>
        </p>
      )}
      <select
        aria-label={`Person für ${kontoName} wählen`}
        value={auswahl}
        disabled={busy}
        onChange={(ereignis) => {
          setAuswahl(ereignis.target.value);
        }}
      >
        <option value="">Person wählen …</option>
        {optionen.map((option) => (
          <option
            key={option.id}
            value={option.id}
            disabled={option.verknuepftMit !== null && option.verknuepftMit !== principal.id}
          >
            {option.name}
            {option.verknuepftMit !== null && option.verknuepftMit !== principal.id
              ? ' (schon verknüpft)'
              : ''}
          </option>
        ))}
      </select>
      <button
        type="button"
        disabled={busy || auswahl === ''}
        onClick={() => {
          onLinkEmployee(principal.id, auswahl);
          setAuswahl('');
        }}
      >
        verknüpfen
      </button>
    </td>
  );
}

/**
 * Eine Zeile mit eigenem lokalem Zustand (die Dropdown-Auswahl) — das ist kein
 * Widerspruch zu „reine Ansicht": gemeint ist, dass die Komponente selbst
 * weder Daten laedt noch Server-Aufrufe absetzt, nicht, dass ein
 * Formularelement ohne lokalen Zustand auskommen muesste.
 */
function PrincipalRow({
  principal,
  roles,
  busy,
  rowError,
  onLock,
  onUnlock,
  onAssignRole,
  onRevokeRole,
  optionen,
  onLinkEmployee,
}: PrincipalRowProps): JSX.Element {
  const [ausgewaehlteRolle, setAusgewaehlteRolle] = useState('');
  const zuweisbareRollen = roles.filter(
    (rolle) => !principal.roles.some((zugewiesen) => zugewiesen.id === rolle.id),
  );

  return (
    <tr>
      <td>{principal.displayName ?? principal.subject}</td>
      <td>{principal.email ?? '—'}</td>
      <PersonZelle
        principal={principal}
        busy={busy}
        optionen={optionen}
        onLinkEmployee={onLinkEmployee}
      />
      <td data-testid={`status-${principal.subject}`}>
        {principal.isActive ? 'aktiv' : 'gesperrt'}
      </td>
      <td>
        <ul>
          {principal.roles.map((rolle) => (
            <li key={rolle.id}>
              {rolle.name}{' '}
              <button
                type="button"
                disabled={busy}
                onClick={() => {
                  onRevokeRole(principal.id, rolle.id);
                }}
              >
                entziehen
              </button>
            </li>
          ))}
        </ul>
        {zuweisbareRollen.length === 0 ? null : (
          <>
            <select
              aria-label={`Rolle für ${principal.displayName ?? principal.subject} zuweisen`}
              value={ausgewaehlteRolle}
              disabled={busy}
              onChange={(ereignis) => {
                setAusgewaehlteRolle(ereignis.target.value);
              }}
            >
              <option value="">Rolle wählen …</option>
              {zuweisbareRollen.map((rolle) => (
                <option key={rolle.id} value={rolle.id}>
                  {rolle.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              disabled={busy || ausgewaehlteRolle === ''}
              onClick={() => {
                onAssignRole(principal.id, ausgewaehlteRolle);
                setAusgewaehlteRolle('');
              }}
            >
              zuweisen
            </button>
          </>
        )}
      </td>
      <td>
        <button
          type="button"
          disabled={busy}
          data-testid={`sperr-knopf-${principal.subject}`}
          onClick={() => {
            if (principal.isActive) {
              onLock(principal.id);
            } else {
              onUnlock(principal.id);
            }
          }}
        >
          {principal.isActive ? 'Sperren' : 'Entsperren'}
        </button>
      </td>
      <td role={rowError === undefined ? undefined : 'alert'}>{rowError}</td>
    </tr>
  );
}

export interface AdminPanelProps {
  /**
   * Ob die Verwaltung geoeffnet ist. Solange nicht, wird NICHTS geladen —
   * und das ist der Punkt (Review-Befund zu PR #51): `principals.list` ohne
   * `principals:read` endet in einem 403, und jedes 403 schreibt seit AC-007
   * eine unloeschbare Audit-Zeile. Bei jedem Seitenaufruf jeder Person ohne
   * Recht waere das genau das Wachstum, vor dem CLAUDE.md §19 warnt. Ein
   * BEWUSSTER Klick ohne Recht wird dagegen protokolliert, wie AC-007 es will.
   */
  readonly geoeffnet: boolean;
  readonly onOeffnen: () => void;
  readonly principals: readonly Principal[];
  /** Die Rollen des Mandanten, aus `tenant.settings` — fürs Zuweisen-Dropdown. */
  readonly roles: readonly TenantRole[];
  readonly loading: boolean;
  /**
   * Eine fertig formulierte Meldung (aus `klassifiziereFehler`), nicht der
   * rohe Fehler — die Übersetzung ist Sache des Aufrufers, derselbe Schnitt
   * wie bei `TrpcPanel`/`SessionPanel`.
   */
  readonly error: string | null;
  /** Kontoeigene Sperrknöpfe, während die zugehörige Aktion noch läuft. */
  readonly busyIds: ReadonlySet<string>;
  /** Die letzte fehlgeschlagene Aktion je Konto, zur Anzeige in dessen Zeile. */
  readonly rowErrors: ReadonlyMap<string, string>;
  readonly onLock: (principalId: string) => void;
  readonly onUnlock: (principalId: string) => void;
  readonly onAssignRole: (principalId: string, roleId: string) => void;
  readonly onRevokeRole: (principalId: string, roleId: string) => void;
  /**
   * Interne Mitarbeiter zur Auswahl (AC-107-1). `null` ohne `principals:manage`:
   * dann zeigt die Spalte „Person“ nur an.
   */
  readonly mitarbeiterOptionen: readonly KontoMitarbeiterOption[] | null;
  /** Die Auswahl ist gescheitert; die Spalte zeigt dann nur an (B5). */
  readonly optionenFehler?: string | null;
  readonly onLinkEmployee: (principalId: string, employeeId: string | null) => void;
}

/**
 * Nutzerverwaltung (AC-107): Konten sperren/entsperren, Rollen zuweisen/entziehen.
 *
 * Reine Ansicht wie `SessionPanel`/`TrpcPanel`: sie lädt selbst nichts und ruft
 * selbst keine Mutation auf. `App.tsx` lädt die Daten, serialisiert die
 * Schreibzugriffe je Konto (CLAUDE.md §20.2, Punkt 4) und übernimmt nach einem
 * erfolgreichen Aufruf die SERVERANTWORT in den Zustand (AC-016, AK-2) —
 * nichts davon gehört in eine Ansicht.
 *
 * KEINE eigene Rechteprüfung: steht die Seite, obwohl das Konto
 * `principals:read` nicht hat, meldet der Server `FORBIDDEN`, und `error`
 * trägt die entsprechende Meldung. Ein Rollenumschalter, der die Seite vorher
 * ausblendet, ist AC-008 und existiert noch nicht.
 */
export function AdminPanel({
  geoeffnet,
  onOeffnen,
  principals,
  roles,
  loading,
  error,
  busyIds,
  rowErrors,
  onLock,
  onUnlock,
  onAssignRole,
  onRevokeRole,
  mitarbeiterOptionen,
  optionenFehler = null,
  onLinkEmployee,
}: AdminPanelProps): JSX.Element {
  if (!geoeffnet) {
    return (
      <section>
        <button type="button" onClick={onOeffnen}>
          Nutzerverwaltung öffnen
        </button>
      </section>
    );
  }

  if (loading) {
    return <p>Konten werden geladen …</p>;
  }

  if (error !== null) {
    return <p role="alert">{error}</p>;
  }

  if (principals.length === 0) {
    return <p>Keine Konten vorhanden.</p>;
  }

  return (
    <section>
      <h2>Nutzerverwaltung</h2>
      {optionenFehler === null ? null : <p role="alert">{optionenFehler}</p>}
      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>E-Mail</th>
            <th>Person</th>
            <th>Status</th>
            <th>Rollen</th>
            <th>Aktion</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {principals.map((principal) => (
            <PrincipalRow
              key={principal.id}
              principal={principal}
              roles={roles}
              busy={busyIds.has(principal.id)}
              rowError={rowErrors.get(principal.id)}
              onLock={onLock}
              onUnlock={onUnlock}
              onAssignRole={onAssignRole}
              onRevokeRole={onRevokeRole}
              optionen={mitarbeiterOptionen}
              onLinkEmployee={onLinkEmployee}
            />
          ))}
        </tbody>
      </table>
    </section>
  );
}
